import { useIsRestoring, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getOfflineDebugSnapshot, subscribeOfflineDebug } from '@/lib/pwa/offlineDebug';
import { getPersistDecision } from '@/lib/pwa/queryPersistence';
import { getUserIdOrNull } from '@/lib/pwa/userScope';

type QueryRow = {
  fetchStatus: string;
  key: string;
  persistReason: string;
  policy: string;
  status: string;
  updatedAt: number;
  willPersist: boolean;
};

function formatDateTime(timestamp: number | null): string {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleTimeString('pt-BR');
}

function formatUpdatedAt(timestamp: number): string {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleTimeString('pt-BR');
}

export function OfflineDebugPanel() {
  const queryClient = useQueryClient();
  const isRestoring = useIsRestoring();
  const debugState = useSyncExternalStore(
    subscribeOfflineDebug,
    getOfflineDebugSnapshot,
    getOfflineDebugSnapshot,
  );
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [cacheNames, setCacheNames] = useState<string[]>([]);
  const [queryRows, setQueryRows] = useState<QueryRow[]>([]);

  const isEnabled = import.meta.env.DEV
    && new URLSearchParams(window.location.search).get('debug-offline') === '1';

  const memorySummary = useMemo(() => {
    const total = queryRows.length;
    const persistable = queryRows.filter((row) => row.willPersist).length;
    return { total, persistable };
  }, [queryRows]);

  useEffect(() => {
    if (!isEnabled) return;

    const refresh = async () => {
      const queries = queryClient.getQueryCache().getAll().map((query) => {
        const decision = getPersistDecision(query);

        return {
          fetchStatus: query.state.fetchStatus,
          key: JSON.stringify(query.queryKey),
          persistReason: decision.reason,
          policy: decision.policy,
          status: query.state.status,
          updatedAt: query.state.dataUpdatedAt,
          willPersist: decision.persisted,
        };
      });

      setQueryRows(queries);

      if (typeof caches === 'undefined') {
        setCacheNames([]);
        return;
      }

      try {
        setCacheNames(await caches.keys());
      } catch {
        setCacheNames([]);
      }
    };

    const syncOnline = () => setIsOnline(navigator.onLine);
    const handleFocus = () => {
      void refresh();
    };
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      void refresh();
    });

    window.addEventListener('online', syncOnline);
    window.addEventListener('offline', syncOnline);
    window.addEventListener('focus', handleFocus);

    void refresh();

    return () => {
      unsubscribe();
      window.removeEventListener('online', syncOnline);
      window.removeEventListener('offline', syncOnline);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isEnabled, queryClient]);

  if (!isEnabled) return null;

  return (
    <Card className="border border-dashed bg-card">
      <CardHeader>
        <CardTitle className="text-base">Debug offline</CardTitle>
        <CardDescription>
          Painel de diagnostico disponivel apenas em desenvolvimento com
          <span className="mx-1 font-mono">?debug-offline=1</span>
          na URL.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div><span className="font-medium">Online:</span> {isOnline ? 'sim' : 'nao'}</div>
          <div><span className="font-medium">isRestoring:</span> {isRestoring ? 'sim' : 'nao'}</div>
          <div><span className="font-medium">Status restore:</span> {debugState.lastRestoreStatus}</div>
          <div><span className="font-medium">Generation:</span> {debugState.restoreGeneration}</div>
          <div><span className="font-medium">User scope:</span> {getUserIdOrNull() ?? '-'}</div>
          <div><span className="font-medium">Buster:</span> {debugState.persistBuster}</div>
          <div><span className="font-medium">Inicio restore:</span> {formatDateTime(debugState.lastRestoreStartedAt)}</div>
          <div><span className="font-medium">Fim restore:</span> {formatDateTime(debugState.lastRestoreFinishedAt)}</div>
          <div><span className="font-medium">Timeout restore:</span> {debugState.restoreTimeoutMs}ms</div>
          <div><span className="font-medium">Queries em memoria:</span> {memorySummary.total}</div>
          <div><span className="font-medium">Queries persistiveis:</span> {memorySummary.persistable}</div>
          <div><span className="font-medium">Erro restore:</span> {debugState.lastRestoreError ?? '-'}</div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Cache Storage</p>
          <div className="rounded-md bg-muted/50 p-3 text-xs font-mono">
            {cacheNames.length > 0 ? cacheNames.join('\n') : 'Nenhum cache encontrado'}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Ultimas decisoes de persistencia</p>
          <div className="rounded-md bg-muted/50 p-3 text-xs font-mono">
            {debugState.lastPersistEvents.length > 0
              ? debugState.lastPersistEvents.map((event) => (
                <div key={`${event.at}-${event.queryKey}`}>
                  [{formatDateTime(event.at)}] {event.persisted ? 'keep' : 'skip'} {event.policy} {event.reason} {event.queryKey}
                </div>
              ))
              : 'Nenhuma decisao registrada ainda'}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Queries observadas</p>
          <div className="rounded-md bg-muted/50 p-3 text-xs font-mono">
            {queryRows.length > 0
              ? queryRows.map((row) => (
                <div key={row.key}>
                  [{row.status}/{row.fetchStatus}] {row.willPersist ? 'persist' : 'skip'} {row.policy} {row.persistReason} {row.key} @ {formatUpdatedAt(row.updatedAt)}
                </div>
              ))
              : 'Nenhuma query em memoria'}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default OfflineDebugPanel;
